import { useSelector } from 'react-redux'
import { activeClaimPerformedSelector } from '@redux/slices/claimStatusSlice'
import {
  authenticatedSelector,
  isAnonymousUserSelector,
  isGuestUserSelector,
  sessionReadySelector,
} from '@redux/slices/connectorSlice'
import { isInsuranceMemberFoundStatus, isInsuranceMemberNoBenefitsAvailable } from '@utils/insuranceMemberUtil'
import { ServiceStatus, ServiceType } from '../types'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { getInsuranceToggleState } from '@utils/insuranceToggleUtil'

type UseServiceStatusReturn = Record<ServiceType, ServiceStatus | undefined>

export const useServiceStatus = (): UseServiceStatusReturn => {
  const isGuest = useSelector(isGuestUserSelector)
  const isAnonymousUser = useSelector(isAnonymousUserSelector)
  const isAuthenticated = useSelector(authenticatedSelector)
  const isSessionReady = useSelector(sessionReadySelector)
  const performedActiveCheck = useSelector(activeClaimPerformedSelector)
  const { claimRecord, claimRecordId, claimRecordStatus, claimRecordDeleted } = useClaimRecord()

  const validSession = (isAuthenticated || isGuest) && isSessionReady

  const getProductQuoteServiceStatus = (): ServiceStatus | undefined => {
    if (isAnonymousUser) {
      return 'unavailable'
    }

    if (validSession && performedActiveCheck) {
      // if claim record exists, determine status from toggle state
      // otherwise service is unavailable if not synced
      if (claimRecordId && claimRecord) {
        const { toggleOn } = getInsuranceToggleState({ claimRecord, claimRecordDeleted })
        const isReady = toggleOn && !isInsuranceMemberNoBenefitsAvailable(claimRecordStatus)
        return isReady ? 'ready' : 'unavailable'
      } else if (!claimRecordId) {
        return 'unavailable'
      }
    }
  }

  const getBenefitPlanServiceStatus = (): ServiceStatus | undefined => {
    if (isAnonymousUser) {
      return 'unavailable'
    }

    if (validSession && performedActiveCheck) {
      if (claimRecordId && claimRecordStatus) {
        const memberFound = isInsuranceMemberFoundStatus(claimRecordStatus)
        return memberFound ? 'ready' : 'unavailable'
      } else if (!claimRecordId) {
        return 'unavailable'
      }
    }
  }

  return {
    'benefit-plan': getBenefitPlanServiceStatus(),
    'product-quote': getProductQuoteServiceStatus(),
    'insurance-data': getBenefitPlanServiceStatus(), // Note: using same status as benefitPlanService
  }
}
